import { useEffect } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { useSWRConfig } from "swr";

const { VITE_CATALOG_API } = import.meta.env;

const WRITE_DATASET_DESCRIPTION_TOOL = "write_dataset_description";

/**
 * When the data-prep agent writes a dataset's description, revalidate that
 * dataset at run end so its nextgen Overview page and the session sidebar
 * (both read through `useDataset`) show the new text without a reload.
 *
 * Mirrors `useProjectDatasetSync` (run-end revalidation via `useSWRConfig`)
 * and `useInitializationPanelSync` (`onToolCallEndEvent` tool-name filter).
 * Keyed off the terminal run event, not `onToolCallEndEvent`: that event
 * fires once the tool-call arguments finish streaming, before the tool runs.
 */
export function useDatasetDescriptionSync(agent: AbstractAgent) {
  const { mutate } = useSWRConfig();
  useEffect(() => {
    const writtenDatasetIds = new Set<string>();
    const onRunEnd = () => {
      if (writtenDatasetIds.size === 0) return;
      const ids = Array.from(writtenDatasetIds);
      writtenDatasetIds.clear();
      void Promise.all(ids.map((id) => mutate(`${VITE_CATALOG_API}/datasets/${id}`))).catch(
        (err) => {
          console.error("[useDatasetDescriptionSync] Failed to refresh dataset:", err);
        }
      );
    };
    const sub = agent.subscribe({
      onToolCallEndEvent: ({ toolCallName, toolCallArgs }) => {
        if (toolCallName !== WRITE_DATASET_DESCRIPTION_TOOL) return;
        const datasetId = toolCallArgs.dataset_id;
        if (typeof datasetId !== "string") return;
        writtenDatasetIds.add(datasetId);
      },
      onRunFinishedEvent: onRunEnd,
      onRunErrorEvent: onRunEnd,
    });
    return () => sub.unsubscribe();
  }, [agent, mutate]);
}
